import React, { useCallback, useLayoutEffect, useMemo, useRef } from "react";

import uniq from "lodash/uniq";

import { ColDef, IHeaderParams } from "ag-grid-community";
import { components, MenuListProps } from "react-select";
import styled from "styled-components/macro";

import AgGrid from "components/AgGrid/AgGrid";
import {
  FieldConfiguration,
  getSideLoadedFieldConfigurationsForUser,
} from "components/Business/SideLoadedBusinesses/CompareModal/fieldConfigurations";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { Column } from "components/Layout";
import { ReactSelect } from "components/SearchableSelector";

import { useTheme } from "hooks";

import {
  collectsManyColumns,
  slotIndicesFor,
} from "lib/sideLoadedFieldMapping";

interface RowCountStatusPanelParams {
  shownCount: number;
  totalCount: number;
}

function RowCountStatusPanel({
  shownCount,
  totalCount,
}: RowCountStatusPanelParams) {
  return (
    <div className="ag-status-name-value">
      Showing {shownCount} of {totalCount} rows
    </div>
  );
}

const HeaderName = styled.div`
  font-weight: bold;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HeaderSelect = styled(ReactSelect)<{ $isMapped: boolean }>`
  font-size: 11px;

  .react-select__control {
    min-height: 26px;
    ${({ $isMapped, theme }) =>
      $isMapped ? "" : `border: 1px dashed ${theme.colors.gray44};`}
  }
  .react-select__value-container,
  .react-select__indicators {
    min-height: 26px;
    padding-top: 0;
    padding-bottom: 0;
  }
  .react-select__indicator {
    padding: 4px;
  }
`;

interface TargetFieldOption {
  value: string;
  label: string;
  // The label within its subgroup, shown in the menu and once selected.
  shortLabel?: string;
  // Its group headings then label, eg General · Address · City.
  path: string;
  isInSubgroup?: boolean;
  takenBy?: string;
  // An additive target with no slot left for this column.
  isFull?: boolean;
  slotCount?: number;
}

interface TargetFieldGroup {
  label: string;
  isSubgroup: boolean;
  options: TargetFieldOption[];
}

// A field group, then its subgroups indented under it. A group made only of
// subgroups (eg Contacts) lists them as top-level groups instead.
function groupOptions(
  configurations: FieldConfiguration[],
  optionByValue: Record<string, TargetFieldOption>,
): TargetFieldGroup[] {
  const groups: TargetFieldGroup[] = [];
  uniq(configurations.map(configuration => configuration.group)).forEach(
    group => {
      const inGroup = configurations.filter(
        configuration => configuration.group === group,
      );
      const direct = inGroup.filter(configuration => !configuration.subgroup);
      if (direct.length) {
        groups.push({
          label: group,
          isSubgroup: false,
          options: direct.map(({ value }) => optionByValue[value]),
        });
      }
      uniq(inGroup.map(configuration => configuration.subgroup))
        .filter(Boolean)
        .forEach(subgroup => {
          groups.push({
            label: subgroup,
            isSubgroup: direct.length > 0,
            options: inGroup
              .filter(configuration => configuration.subgroup === subgroup)
              .map(({ value }) => ({
                ...optionByValue[value],
                isInSubgroup: direct.length > 0,
              })),
          });
        });
    },
  );
  return groups;
}

const OptionMeta = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.gray44};
`;

function formatOptionLabel(option: TargetFieldOption): React.ReactNode {
  const label = option.shortLabel || option.label;
  const meta = option.isFull
    ? `All ${option.slotCount} slots used`
    : option.takenBy && `Mapped to ${option.takenBy}`;
  return meta ? (
    <>
      {label}
      <OptionMeta>{meta}</OptionMeta>
    </>
  ) : (
    label
  );
}

const SUBGROUP_INDENT = 24;
// Fixed so a subgroup's heading can stick just below its parent's.
const GROUP_HEADING_HEIGHT = 24;

// Wraps a group with its subgroups so the headings stick within the whole
// section and stack, rather than each within its own options.
function GroupedMenuList(props: MenuListProps) {
  const sectionsRef = useRef<HTMLDivElement>(null);
  // react-select's own scroll runs before the portalled menu exists.
  useLayoutEffect(() => {
    const menuList = sectionsRef.current?.parentElement;
    const selected = sectionsRef.current?.querySelector<HTMLElement>(
      ".react-select__option--is-selected",
    );
    if (menuList && selected) {
      menuList.scrollTop =
        selected.offsetTop -
        (menuList.clientHeight - selected.offsetHeight) / 2;
    }
  }, []);

  const sections: React.ReactNode[][] = [];
  React.Children.forEach(props.children, child => {
    const group = React.isValidElement<{ data?: TargetFieldGroup }>(child)
      ? child.props.data
      : undefined;
    if (group?.isSubgroup && sections.length) {
      sections[sections.length - 1].push(child);
    } else {
      sections.push([child]);
    }
  });
  return (
    <components.MenuList {...props}>
      <div ref={sectionsRef}>
        {sections.map((section, index) => (
          <div key={index}>{section}</div>
        ))}
      </div>
    </components.MenuList>
  );
}

// Through react-select's hook, not CSS - its option classes outrank stylesheets.
function getOptionStyles(theme) {
  return {
    option: (base, state: { data: TargetFieldOption; isFocused: boolean }) => ({
      ...base,
      paddingLeft: state.data.isInSubgroup ? SUBGROUP_INDENT : base.paddingLeft,
      color: state.isFocused
        ? base.color
        : state.data.takenBy || state.data.isFull
          ? theme.colors.gray44
          : theme.colors.titleDark,
    }),
    // The menu's top padding would leave a gap above a stuck heading.
    menuList: base => ({ ...base, paddingTop: 0 }),
    // Contents, so its heading sticks within the section, not the group.
    group: base => ({ ...base, display: "contents" }),
    groupHeading: (base, state: { data: TargetFieldGroup }) => ({
      ...base,
      position: "sticky",
      top: state.data.isSubgroup ? GROUP_HEADING_HEIGHT : 0,
      zIndex: state.data.isSubgroup ? 1 : 2,
      boxSizing: "border-box",
      height: GROUP_HEADING_HEIGHT,
      lineHeight: `${GROUP_HEADING_HEIGHT - 8}px`,
      marginBottom: 0,
      paddingTop: 4,
      paddingBottom: 4,
      backgroundColor: theme.colors.white,
      paddingLeft: state.data.isSubgroup ? SUBGROUP_INDENT : base.paddingLeft,
      textTransform: state.data.isSubgroup ? "none" : base.textTransform,
    }),
  };
}

interface MappingHeaderParams extends IHeaderParams {
  // The file's header, not displayName - ag-grid humanises that and only the raw
  // one keys a row.
  sourceColumn: string;
  configurations: FieldConfiguration[];
  targetFieldKey: string | null;
  columnByTarget: Record<string, string>;
  additiveTargets: Set<string>;
  fullAdditiveTargets: Set<string>;
  onChangeTarget: (sourceColumn: string, targetFieldKey: string | null) => void;
}

function MappingHeader(params: MappingHeaderParams) {
  const {
    sourceColumn,
    configurations,
    targetFieldKey,
    columnByTarget,
    additiveTargets,
    fullAdditiveTargets,
    onChangeTarget,
  } = params;
  const theme = useTheme();
  const optionStyles = useMemo(() => getOptionStyles(theme), [theme]);

  const options = useMemo<TargetFieldOption[]>(
    () =>
      configurations.map(configuration => {
        const takenBy = columnByTarget[configuration.value];
        const takenElsewhere =
          Boolean(takenBy) &&
          takenBy !== sourceColumn &&
          !additiveTargets.has(configuration.value);
        return {
          value: configuration.value,
          label: configuration.label,
          shortLabel: configuration.shortLabel,
          path: [
            configuration.group,
            configuration.subgroup,
            configuration.shortLabel || configuration.label,
          ]
            .filter(Boolean)
            .join(" · "),
          takenBy: takenElsewhere ? takenBy : undefined,
          // Still offered to columns holding a slot, so one can be re-pointed directly.
          isFull:
            fullAdditiveTargets.has(configuration.value) &&
            targetFieldKey !== configuration.value,
          slotCount: slotIndicesFor(configuration).length,
        };
      }),
    [
      configurations,
      columnByTarget,
      sourceColumn,
      additiveTargets,
      fullAdditiveTargets,
      targetFieldKey,
    ],
  );

  const groupedOptions = useMemo(
    () =>
      groupOptions(
        configurations,
        Object.fromEntries(options.map(option => [option.value, option])),
      ),
    [configurations, options],
  );

  // From the grouped options, as react-select finds it by reference to focus
  // it when the menu opens.
  const selectedOption = groupedOptions
    .flatMap(group => group.options)
    .find(option => option.value === targetFieldKey);

  return (
    <Column
      full
      // Drag-to-reorder starts on mousedown, so stopping the click is too late.
      onMouseDown={event => event.stopPropagation()}
      onTouchStart={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
    >
      <HeaderName title={sourceColumn}>{sourceColumn}</HeaderName>
      {/* On a wrapper: react-select's input sits over the value, hiding its title. */}
      <div title={selectedOption?.path}>
        <HeaderSelect
          $isMapped={Boolean(targetFieldKey)}
          value={selectedOption || null}
          options={groupedOptions}
          formatOptionLabel={formatOptionLabel}
          isOptionDisabled={(option: TargetFieldOption) =>
            Boolean(option.isFull)
          }
          styles={optionStyles}
          components={{ MenuList: GroupedMenuList }}
          onChange={option =>
            onChangeTarget(sourceColumn, option?.value || null)
          }
          placeholder="Not mapped..."
          isClearable
          menuPortalTarget={document.body}
          menuPlacement="auto"
        />
      </div>
    </Column>
  );
}

interface ColumnMappingGridProps {
  sourceColumns: string[];
  rows: Record<string, unknown>[];
  // The real row count; rows is only ever a preview.
  totalRowCount: number;
  entries: SideLoadedDataMappingEntry[];
  onChange: (entries: SideLoadedDataMappingEntry[]) => void;
  userRoleContentTypeName: string | null;
}

// From sourceColumns, not generated from rows - generation samples the first row,
// which is wrong the moment one is missing a key.
export function ColumnMappingGrid({
  sourceColumns,
  rows,
  totalRowCount,
  entries,
  onChange,
  userRoleContentTypeName,
}: ColumnMappingGridProps): React.JSX.Element {
  const configurations = useMemo(
    () => getSideLoadedFieldConfigurationsForUser(userRoleContentTypeName),
    [userRoleContentTypeName],
  );

  const additiveTargets = useMemo(
    () =>
      new Set(
        configurations
          .filter(collectsManyColumns)
          .map(configuration => configuration.value),
      ),
    [configurations],
  );

  // Only as many columns as the compare modal has slots; extras were dropped.
  const fullAdditiveTargets = useMemo(() => {
    const countByTarget = entries.reduce<Record<string, number>>(
      (counts, entry) => {
        if (additiveTargets.has(entry.targetFieldKey)) {
          counts[entry.targetFieldKey] =
            (counts[entry.targetFieldKey] || 0) + 1;
        }
        return counts;
      },
      {},
    );

    return new Set(
      configurations
        .filter(
          configuration =>
            (countByTarget[configuration.value] || 0) >=
            slotIndicesFor(configuration).length,
        )
        .map(configuration => configuration.value),
    );
  }, [entries, additiveTargets, configurations]);

  const targetByColumn = useMemo(
    () =>
      entries.reduce<Record<string, string>>((byColumn, entry) => {
        byColumn[entry.sourceColumn] = entry.targetFieldKey;
        return byColumn;
      }, {}),
    [entries],
  );

  const columnByTarget = useMemo(
    () =>
      entries.reduce<Record<string, string>>((byTarget, entry) => {
        // First wins, matching how an additive target reads its columns in order.
        if (!byTarget[entry.targetFieldKey]) {
          byTarget[entry.targetFieldKey] = entry.sourceColumn;
        }
        return byTarget;
      }, {}),
    [entries],
  );

  const onChangeTarget = useCallback(
    (sourceColumn: string, targetFieldKey: string | null) => {
      const withoutColumn = entries.filter(
        entry => entry.sourceColumn !== sourceColumn,
      );
      if (!targetFieldKey) {
        onChange(withoutColumn);
        return;
      }
      // A non-additive target takes one column, so re-pointing it moves the column.
      const freed = additiveTargets.has(targetFieldKey)
        ? withoutColumn
        : withoutColumn.filter(
            entry => entry.targetFieldKey !== targetFieldKey,
          );
      // Rebuilt in file order, which is what decides the notes slots later.
      const next = freed.concat({ sourceColumn, targetFieldKey });
      onChange(
        sourceColumns
          .map(column => next.find(entry => entry.sourceColumn === column))
          .filter(Boolean),
      );
    },
    [entries, onChange, sourceColumns, additiveTargets],
  );

  const columnDefs = useMemo<ColDef[]>(
    () =>
      sourceColumns.map(sourceColumn => ({
        colId: sourceColumn,
        field: sourceColumn,
        headerComponent: MappingHeader,
        headerComponentParams: {
          sourceColumn,
          configurations,
          targetFieldKey: targetByColumn[sourceColumn] || null,
          columnByTarget,
          additiveTargets,
          fullAdditiveTargets,
          onChangeTarget,
        },
        minWidth: 170,
        // File order decides the notes slots.
        suppressMovable: true,
        sortable: false,
      })),
    [
      sourceColumns,
      configurations,
      targetByColumn,
      columnByTarget,
      additiveTargets,
      fullAdditiveTargets,
      onChangeTarget,
    ],
  );

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: RowCountStatusPanel,
          statusPanelParams: {
            shownCount: rows.length,
            totalCount: totalRowCount,
          },
          align: "left",
        },
      ],
    }),
    [rows.length, totalRowCount],
  );

  return (
    <ModalTableWrapper minHeight={320}>
      <AgGrid
        columnDefs={columnDefs}
        rowData={rows}
        headerHeight={68}
        statusBar={statusBar}
      />
    </ModalTableWrapper>
  );
}
